import type {
  AgentSession,
  AgentSessionsQuery,
  AgentSessionsResponse,
  AssignProjectBody,
  AssignProjectResponse,
  PublishAllBody,
} from 'api/agentSessions/agentSessions.api';
import {
  assignAgentSessionProject,
  deleteAgentSession,
  publishAllAgentSessions,
  setAgentSessionPublished,
} from 'api/agentSessions/agentSessions.api';
import {
  invalidateAgentSessions,
  loadAgentSessions,
  loadAgentSessionsProgressively,
  peekAgentSessions,
} from 'api/agentSessions/agentSessions.loading';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  type AgentPeriod,
  applyReviewResponse,
  applySharing,
  applySuggestionResponse,
  clearLiveBadges,
  clearLiveChanges,
  markLiveChanges,
  toAgentPeriods,
} from './agentTracking.model';

import { captureRowPositions } from './liveMotion';

/** How long a row stays marked after a refresh changed it; the highlight runs for as long. */
export const LIVE_CHANGE_MS = 1600;

export interface AgentSessionsState {
  periods: AgentPeriod[];
  hasUploadedSessions: boolean | null | undefined;
  /**
   * Send a request made outside this hook that changes one session (accepting
   * or undoing project suggestions, or reading it back after predictions) in
   * turn with the other mutations, then apply the project assignment,
   * suggestions and sharing it returns. Resolves with that session.
   */
  changeSession: (request: () => Promise<AgentSession>) => Promise<AgentSession>;
  loading: boolean;
  complete: boolean;
  error: string | null;
  /** The backend capped the session list; the window is incomplete. */
  truncated: boolean;
  /** Fetch the current window again; the loaded rows stay until the response lands. */
  reload: () => void;
  /**
   * Read the window again in the background and mark the periods that changed,
   * for changes made elsewhere (uploads, predictions). Resolves false without
   * reading while a mutation or the window's own load is in flight, so the
   * caller can try again.
   */
  refresh: () => Promise<boolean>;
  /** When a live refresh last landed (ms), or 0; totals count up for changes it brought. */
  liveChangedAt: number;
  /** Remove the entire session after server confirmation. */
  deleteSession: (sessionId: number) => Promise<void>;
  /**
   * Assign (or clear with null) the project on every period of a session, or
   * only on `segmentIds`. Resolves with the confirmed session after the server
   * confirmed; the loaded periods are patched in place with the fields the
   * patch sent. Rejects with the server's error.
   */
  assignReview: (sessionId: number, patch: AssignProjectBody) => Promise<AgentSession>;
  assignProject: (
    sessionId: number,
    projectId: number | null,
    segmentIds?: number[]
  ) => Promise<void>;
  /**
   * Publish a session, or make it private again, for its current and future
   * periods. Resolves with the confirmed session after the server confirmed;
   * the loaded periods are patched in place. Rejects with the server's error.
   */
  setPublished: (sessionId: number, published: boolean) => Promise<AgentSession>;
  /**
   * Publish every unpublished session of the signed-in user in this workspace,
   * including the ones they made private only when `body` asks for them.
   * Resolves with the number of sessions published, after the server confirmed;
   * the current window is then fetched again.
   */
  publishAll: (body?: PublishAllBody) => Promise<number>;
}

export interface AgentSessionsFetchers {
  remove?: (sessionId: number) => Promise<void>;
  fetchSessions?: (
    query: AgentSessionsQuery,
    onPage?: (response: AgentSessionsResponse) => void
  ) => Promise<AgentSessionsResponse>;
  assign?: (sessionId: number, body: AssignProjectBody) => Promise<AssignProjectResponse>;
  publish?: typeof setAgentSessionPublished;
  publishAll?: typeof publishAllAgentSessions;
}

/**
 * Sessions the user deleted during this page lifetime, keyed by backend id.
 * Module-level on purpose: a view that remounts (leaving a profile tab and
 * coming back) starts a new instance, and its GET must still not resurrect a
 * session whose DELETE is confirming in the old one. Session ids are globally
 * unique, so sharing across workspaces is harmless.
 */
const deletedSessionIds = new Set<number>();
/** In-flight deletes, so two views cannot DELETE the same session twice. */
const pendingDeletes = new Map<number, Promise<void>>();

/** Test hook: forget remembered deletions between tests. */
export function resetDeletedSessionMemory() {
  deletedSessionIds.clear();
  pendingDeletes.clear();
}

/**
 * Loads the signed-in user's agent activity for one day window, or with userId one
 * colleague's (what the viewer may see of it, TT-6346), and flattens it to periods. The fetchers are injectable so tests can supply their own.
 * Mutations (assign, delete, publish, suggestion changes) run one at a time
 * per hook instance, so their responses are applied in request order.
 */
export function useAgentSessions(
  { from, to, userId }: AgentSessionsQuery,
  {
    fetchSessions: suppliedFetchSessions,
    assign = assignAgentSessionProject,
    remove = deleteAgentSession,
    publish = setAgentSessionPublished,
    publishAll: publishAllRequest = publishAllAgentSessions,
  }: AgentSessionsFetchers = {},
  progressive = false,
  /** Called with the project a confirmed assignment set, so it can lead "Recent". */
  onProjectUsed?: (projectIds: number[]) => void
): AgentSessionsState {
  const fetchSessions =
    suppliedFetchSessions ?? (progressive ? loadAgentSessionsProgressively : loadAgentSessions);
  // Tail of the in-flight mutation chain, or null when idle.
  const mutationChain = useRef<Promise<unknown> | null>(null);
  const revision = useRef(0);
  // Changes the server confirmed while the current list request is in flight,
  // in order, or null when none is. That response cannot contain them, so they
  // are applied again to it; each one touches only the fields its mutation set.
  const missedByLoad = useRef<((periods: AgentPeriod[]) => AgentPeriod[])[] | null>(null);
  // Bumped to fetch the current window again without leaving it.
  const [reloadToken, setReloadToken] = useState(0);
  // The window the rows belong to: another window or fetcher drops them, a
  // reload of the same one keeps them on screen until the new response lands.
  const shownWindow = useRef<{
    from: string;
    to: string;
    userId?: number;
    fetchSessions: unknown;
  } | null>(null);
  const queryKey = `${from}:${to}:${userId ?? ''}`;
  const cached =
    fetchSessions === loadAgentSessions || fetchSessions === loadAgentSessionsProgressively
      ? peekAgentSessions({ from, to, userId }, progressive)
      : undefined;
  const [loadedWindow, setLoadedWindow] = useState(queryKey);
  const [periods, setPeriods] = useState<AgentPeriod[]>(() =>
    cached ? toAgentPeriods(cached.sessions).filter(p => !deletedSessionIds.has(p.sessionId)) : []
  );
  const [hasUploadedSessions, setHasUploadedSessions] = useState(cached?.has_uploaded_sessions);
  const [loading, setLoading] = useState(!cached);
  const [complete, setComplete] = useState(!!cached && !cached.next_cursor);
  const [error, setError] = useState<string | null>(null);
  const [truncated, setTruncated] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reloadToken refetches the same window
  useEffect(() => {
    let cancelled = false;
    const initial =
      fetchSessions === loadAgentSessions || fetchSessions === loadAgentSessionsProgressively
        ? peekAgentSessions({ from, to, userId }, progressive)
        : undefined;
    const shown = shownWindow.current;
    shownWindow.current = { from, to, userId, fetchSessions };
    setLoadedWindow(`${from}:${to}:${userId ?? ''}`);
    if (
      !shown ||
      shown.from !== from ||
      shown.to !== to ||
      shown.userId !== userId ||
      shown.fetchSessions !== fetchSessions
    ) {
      setPeriods(
        initial
          ? toAgentPeriods(initial.sessions).filter(p => !deletedSessionIds.has(p.sessionId))
          : []
      );
      setHasUploadedSessions(initial?.has_uploaded_sessions);
      setTruncated(initial?.truncated ?? false);
      setComplete(!!initial && !initial.next_cursor);
    }
    const missed: ((periods: AgentPeriod[]) => AgentPeriod[])[] = [];
    missedByLoad.current = missed;
    // Once the request completes, fails or is cancelled, nothing can miss these changes.
    const settle = () => {
      missed.length = 0;
      if (missedByLoad.current === missed) missedByLoad.current = null;
    };
    const load = async () => {
      setLoading(!initial);
      setError(null);
      try {
        const apply = (response: AgentSessionsResponse) => {
          if (cancelled) return;
          setPeriods(
            missed.reduce(
              (periods, change) => change(periods),
              toAgentPeriods(response.sessions).filter(p => !deletedSessionIds.has(p.sessionId))
            )
          );
          setHasUploadedSessions(response.has_uploaded_sessions);
          setComplete(!response.next_cursor);
          setTruncated(!response.next_cursor && response.truncated);
          setLoading(false);
        };
        const response = await fetchSessions({ from, to, userId }, page => {
          if (!initial || initial.next_cursor) apply(page);
        });
        if (cancelled) return;
        apply(response);
      } catch (e) {
        if (!cancelled) setError((e as Error).message ?? 'Failed to load agent sessions');
      } finally {
        settle();
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
      settle();
    };
  }, [from, to, userId, fetchSessions, progressive, reloadToken]);

  // When a live refresh last landed, so totals know to count (see CountUp).
  const [liveChangedAt, setLiveChangedAt] = useState(0);
  // Bumped when the window changes, so a refresh for the old one is dropped.
  const windowToken = useRef(0);
  // biome-ignore lint/correctness/useExhaustiveDependencies: a new window or fetcher invalidates refreshes in flight
  useEffect(() => {
    windowToken.current++;
  }, [from, to, userId, fetchSessions]);
  const clearTimers = useRef(new Set<ReturnType<typeof setTimeout>>());
  useEffect(() => {
    const timers = clearTimers.current;
    return () => {
      for (const timer of timers) clearTimeout(timer);
      timers.clear();
    };
  }, []);

  // Reads the window again without a loading state and marks what changed, so
  // rows can highlight it. Skipped while a mutation is in flight, and dropped if
  // one started meanwhile: its own response is newer than this read.
  const refresh = useCallback(async () => {
    if (mutationChain.current || missedByLoad.current) return false;
    const before = revision.current;
    const token = windowToken.current;
    const response = await fetchSessions({ from, to, userId });
    if (before !== revision.current || token !== windowToken.current || mutationChain.current)
      return false;
    const at = Date.now();
    captureRowPositions();
    setLiveChangedAt(at);
    setPeriods(previous =>
      markLiveChanges(
        previous,
        toAgentPeriods(response.sessions).filter(p => !deletedSessionIds.has(p.sessionId)),
        at
      )
    );
    setHasUploadedSessions(response.has_uploaded_sessions);
    setTruncated(response.truncated);
    const timer = setTimeout(() => {
      clearTimers.current.delete(timer);
      setPeriods(periods => clearLiveChanges(periods, at));
    }, LIVE_CHANGE_MS);
    clearTimers.current.add(timer);
    return true;
  }, [fetchSessions, from, to, userId]);

  // One timer for the earliest pill to run out, instead of one per row.
  const nextBadgeExpiry = periods.reduce<number | null>(
    (earliest, period) =>
      period.liveBadge && (earliest === null || period.liveBadge.until < earliest)
        ? period.liveBadge.until
        : earliest,
    null
  );
  useEffect(() => {
    if (nextBadgeExpiry === null) return;
    const timer = setTimeout(
      () => setPeriods(current => clearLiveBadges(current, Date.now())),
      Math.max(0, nextBadgeExpiry - Date.now())
    );
    return () => clearTimeout(timer);
  }, [nextBadgeExpiry]);

  const hasPendingPrediction = periods.some(period =>
    ['queued', 'running'].includes(period.projectPredictionState ?? '')
  );
  useEffect(() => {
    if (!hasPendingPrediction || !complete) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      try {
        await refresh();
      } catch {
        // Keep the visible data and retry the read; polling never starts predictions.
      } finally {
        if (!cancelled) timer = setTimeout(poll, 5000);
      }
    };
    timer = setTimeout(poll, 5000);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [hasPendingPrediction, complete, refresh]);

  // Queue a mutation behind the previous one; a failure never blocks the next.
  // Run a mutation after the previous one; a failure never blocks the next.
  // An idle chain starts the request synchronously, so a click reaches the
  // server without waiting for a microtask.
  const enqueue = useCallback(<T>(run: () => Promise<T>): Promise<T> => {
    revision.current++;
    invalidateAgentSessions();
    const tail = mutationChain.current;
    const next = tail ? tail.then(run, run) : run();
    const release = () => {
      invalidateAgentSessions();
      if (mutationChain.current === settled) mutationChain.current = null;
    };
    const settled: Promise<unknown> = next.then(release, release);
    mutationChain.current = settled;
    return next;
  }, []);

  const reload = useCallback(() => setReloadToken(token => token + 1), []);

  // Apply a confirmed change to the loaded periods and to the list response in flight.
  const confirm = useCallback((apply: (periods: AgentPeriod[]) => AgentPeriod[]) => {
    missedByLoad.current?.push(apply);
    setPeriods(apply);
  }, []);

  const changeSession = useCallback(
    (request: () => Promise<AgentSession>) =>
      enqueue(async () => {
        const session = await request();
        confirm(periods => applySuggestionResponse(periods, session));
        return session;
      }),
    [confirm, enqueue]
  );

  const deleteSession = useCallback(
    (sessionId: number) =>
      enqueue(async () => {
        const inFlight = pendingDeletes.get(sessionId);
        if (inFlight) {
          await inFlight;
        } else if (!deletedSessionIds.has(sessionId)) {
          const request = remove(sessionId).finally(() => pendingDeletes.delete(sessionId));
          pendingDeletes.set(sessionId, request);
          await request;
        }
        // Remember before touching state: a GET already in flight must not restore the row.
        deletedSessionIds.add(sessionId);
        setPeriods(previous => previous.filter(p => p.sessionId !== sessionId));
      }),
    [enqueue, remove]
  );

  const assignReview = useCallback(
    (sessionId: number, patch: AssignProjectBody) =>
      enqueue(async () => {
        const response = await assign(sessionId, patch);
        confirm(periods => applyReviewResponse(periods, response.session, patch));
        if (typeof patch.project_id === 'number') onProjectUsed?.([patch.project_id]);
        return response.session;
      }),
    [assign, confirm, enqueue, onProjectUsed]
  );

  const assignProject = useCallback(
    async (sessionId: number, projectId: number | null, segmentIds?: number[]) => {
      await assignReview(sessionId, {
        project_id: projectId,
        ...(segmentIds ? { segment_ids: segmentIds } : {}),
      });
    },
    [assignReview]
  );

  const setPublished = useCallback(
    (sessionId: number, published: boolean) =>
      enqueue(async () => {
        const session = await publish(sessionId, published);
        confirm(periods => applySharing(periods, session));
        return session;
      }),
    [confirm, enqueue, publish]
  );

  const publishAll = useCallback(
    (body?: PublishAllBody) =>
      enqueue(async () => {
        const result = await publishAllRequest(body);
        // Sessions uploaded since the list loaded are missing from it, and the
        // GET in flight may predate the change: fetch the window again.
        reload();
        return result.published_count;
      }),
    [enqueue, publishAllRequest, reload]
  );

  return {
    periods:
      loadedWindow === queryKey
        ? periods
        : cached
          ? toAgentPeriods(cached.sessions).filter(p => !deletedSessionIds.has(p.sessionId))
          : [],
    hasUploadedSessions:
      loadedWindow === queryKey ? hasUploadedSessions : cached?.has_uploaded_sessions,
    loading: loadedWindow === queryKey ? loading : !cached,
    complete: loadedWindow === queryKey ? complete : !!cached && !cached.next_cursor,
    error,
    truncated,
    reload,
    refresh,
    liveChangedAt,
    changeSession,
    assignProject,
    assignReview,
    deleteSession,
    setPublished,
    publishAll,
  };
}
